import { t } from 'i18next'
import { useEffect, useRef, useCallback } from 'react'
import toast from 'react-hot-toast'

const getTimeoutConfig = () => {
  const mode = import.meta.env.VITE_MODE

  if (mode === 'hml' || mode === 'development') {
    return {
      warningTimeout: 60 * 1000, // 1 minuto (1 minuto antes do logout)
      warningDelay: 24 * 60 * 60 * 1000, // 24 horas total
    }
  }

  if (mode === 'production') {
    return {
      warningTimeout: 60 * 1000, // 1 minuto (1 minuto antes do logout)
      warningDelay: 2 * 60 * 60 * 1000, // 2 horas total
    }
  }

  return {
    warningTimeout: 60 * 1000, // 1 minuto (1 minuto antes do logout)
    warningDelay: 2 * 60 * 60 * 1000, // 2 horas total (default)
  }
}

const getCleanupTime = () => {
  const mode = import.meta.env.VITE_MODE

  if (mode === 'hml' || mode === 'development') {
    return 23 * 60 * 60 * 1000 // 23 horas (1 hora antes do timeout de 24 horas)
  }

  if (mode === 'production') {
    return 1 * 60 * 60 * 1000 + 59 * 60 * 1000 // 1h59min (1 minuto antes do timeout de 2 horas)
  }

  return 1 * 60 * 60 * 1000 + 59 * 60 * 1000 // 1h59min (1 minuto antes do timeout de 2 horas - default)
}

const { warningTimeout: WARNING_TIMEOUT, warningDelay: WARNING_DELAY } =
  getTimeoutConfig()
const THROTTLE_DELAY = 2000 // 2 segundos para throttling
const CLEANUP_TIME = getCleanupTime()

const INACTIVITY_KEY = 'inactivity_last_activity'
const TAB_ACTIVITY_KEY = 'inactivity_tab_activity'
const TAB_ID_KEY = 'inactivity_tab_id'
const ACTIVE_TABS_KEY = 'active_tabs'

export function useInactivityTimeout(onLogout: () => void) {
  const inactivityTimer = useRef<number | null>(null)
  const warningTimer = useRef<number | null>(null)
  const isWarningActive = useRef<boolean>(false)
  const currentToastId = useRef<string | null>(null)
  const lastActivityTime = useRef<number>(Date.now())
  const throttleTimeout = useRef<number | null>(null)
  const tabId = useRef<string>(`tab_${Date.now()}_${Math.random()}`)

  const clearTimers = useCallback(() => {
    const timers = [inactivityTimer, warningTimer, throttleTimeout]
    timers.forEach((timerRef) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
    })
  }, [])

  const clearToast = useCallback(() => {
    if (currentToastId.current) {
      toast.dismiss(currentToastId.current)
      currentToastId.current = null
    }
  }, [])

  const resetState = useCallback(() => {
    isWarningActive.current = false
  }, [])

  const registerActiveTab = useCallback(() => {
    try {
      const activeTabs = JSON.parse(
        localStorage.getItem(ACTIVE_TABS_KEY) || '{}'
      )
      activeTabs[tabId.current] = Date.now()

      const cleanupTimeAgo = Date.now() - CLEANUP_TIME
      Object.keys(activeTabs).forEach((tab) => {
        if (activeTabs[tab] < cleanupTimeAgo) {
          delete activeTabs[tab]
        }
      })

      localStorage.setItem(ACTIVE_TABS_KEY, JSON.stringify(activeTabs))
    } catch (error) {
      console.warn('Error registering active tab:', error)
    }
  }, [])

  const unregisterActiveTab = useCallback(() => {
    try {
      const activeTabs = JSON.parse(
        localStorage.getItem(ACTIVE_TABS_KEY) || '{}'
      )
      delete activeTabs[tabId.current]
      localStorage.setItem(ACTIVE_TABS_KEY, JSON.stringify(activeTabs))
    } catch (error) {
      console.warn('Error unregistering active tab:', error)
    }
  }, [])

  const hasOtherActiveTabs = useCallback(() => {
    try {
      const activeTabs = JSON.parse(
        localStorage.getItem(ACTIVE_TABS_KEY) || '{}'
      )
      const currentTime = Date.now()
      const cleanupTimeAgo = currentTime - CLEANUP_TIME

      return Object.keys(activeTabs).some((tab) => {
        if (tab === tabId.current) return false
        return activeTabs[tab] > cleanupTimeAgo
      })
    } catch (error) {
      console.warn('Error checking other active tabs:', error)
      return false
    }
  }, [])

  const syncActivity = useCallback(
    (timestamp: number) => {
      try {
        localStorage.setItem(INACTIVITY_KEY, timestamp.toString())
        localStorage.setItem(TAB_ACTIVITY_KEY, timestamp.toString())
        localStorage.setItem(TAB_ID_KEY, tabId.current)

        registerActiveTab()
      } catch (error) {
        console.warn('localStorage not available for inactivity sync')
      }
    },
    [registerActiveTab]
  )

  const checkOtherTabsInactivity = useCallback(() => {
    try {
      const lastGlobalActivity = localStorage.getItem(INACTIVITY_KEY)
      const lastTabActivity = localStorage.getItem(TAB_ACTIVITY_KEY)
      const lastTabId = localStorage.getItem(TAB_ID_KEY)

      if (lastGlobalActivity && lastTabActivity) {
        const globalTime = parseInt(lastGlobalActivity)
        const tabTime = parseInt(lastTabActivity)
        const now = Date.now()

        if (hasOtherActiveTabs()) {
          return false
        }

        if (now - globalTime > WARNING_DELAY + WARNING_TIMEOUT) {
          return true
        }

        if (now - tabTime > WARNING_DELAY + WARNING_TIMEOUT) {
          return true
        }

        if (
          lastTabId !== tabId.current &&
          now - tabTime > WARNING_DELAY + WARNING_TIMEOUT
        ) {
          return true
        }
      }
    } catch (error) {
      console.warn('Error checking other tabs inactivity:', error)
    }
    return false
  }, [hasOtherActiveTabs])

  const checkCurrentTabInactivity = useCallback(() => {
    try {
      const lastGlobalActivity = localStorage.getItem(INACTIVITY_KEY)
      const lastTabActivity = localStorage.getItem(TAB_ACTIVITY_KEY)

      if (lastGlobalActivity && lastTabActivity) {
        const globalTime = parseInt(lastGlobalActivity)
        const tabTime = parseInt(lastTabActivity)
        const now = Date.now()

        if (now - globalTime > WARNING_DELAY + WARNING_TIMEOUT) {
          return true
        }

        if (now - tabTime > WARNING_DELAY + WARNING_TIMEOUT) {
          return true
        }

        if (now - tabTime > WARNING_DELAY + WARNING_TIMEOUT) {
          return true
        }
      }
    } catch (error) {
      console.warn('Error checking current tab inactivity:', error)
    }
    return false
  }, [])

  const resetTimer = useCallback(() => {
    const now = Date.now()

    if (now - lastActivityTime.current < THROTTLE_DELAY) {
      return
    }

    lastActivityTime.current = now

    syncActivity(now)

    if (isWarningActive.current) {
      clearToast()
      resetState()
      return
    }

    clearTimers()

    if (hasOtherActiveTabs()) {
      return
    }

    inactivityTimer.current = setTimeout(() => {
      isWarningActive.current = true

      const toastId = toast(
        t('warning.inactivity', { time: Math.round(WARNING_TIMEOUT / 60000) }),
        {
          id: 'inactivity-warning',
          duration: WARNING_TIMEOUT,
          position: 'top-right',
        }
      )

      currentToastId.current = toastId

      warningTimer.current = setTimeout(() => {
        toast.dismiss(toastId)
        resetState()
        currentToastId.current = null
        onLogout()
      }, WARNING_TIMEOUT)
    }, WARNING_DELAY)
  }, [
    onLogout,
    clearTimers,
    resetState,
    clearToast,
    syncActivity,
    hasOtherActiveTabs,
  ])

  const handleWindowFocus = useCallback(() => {
    registerActiveTab()

    if (checkCurrentTabInactivity()) {
      onLogout()
      return
    }

    resetTimer()
  }, [checkCurrentTabInactivity, onLogout, resetTimer, registerActiveTab])

  const handlePageShow = useCallback(() => {
    registerActiveTab()

    if (checkCurrentTabInactivity()) {
      onLogout()
      return
    }

    resetTimer()
  }, [checkCurrentTabInactivity, onLogout, resetTimer, registerActiveTab])

  const handleStorageChange = useCallback(
    (event: StorageEvent) => {
      if (event.key === INACTIVITY_KEY || event.key === TAB_ACTIVITY_KEY) {
        if (hasOtherActiveTabs()) {
          if (event.key === INACTIVITY_KEY && event.newValue) {
            const newActivityTime = parseInt(event.newValue)
            if (newActivityTime > lastActivityTime.current) {
              lastActivityTime.current = newActivityTime
              resetTimer()
            }
          }
          return
        }

        if (checkOtherTabsInactivity()) {
          onLogout()
          return
        }

        if (event.key === INACTIVITY_KEY && event.newValue) {
          const newActivityTime = parseInt(event.newValue)
          if (newActivityTime > lastActivityTime.current) {
            lastActivityTime.current = newActivityTime
            resetTimer()
          }
        }
      }
    },
    [checkOtherTabsInactivity, onLogout, resetTimer, hasOtherActiveTabs]
  )

  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible') {
      registerActiveTab()

      if (hasOtherActiveTabs()) {
        resetTimer()
        return
      }

      if (checkOtherTabsInactivity()) {
        onLogout()
        return
      }

      resetTimer()
    } else {
      if (!hasOtherActiveTabs()) {
        return
      }
    }
  }, [
    checkOtherTabsInactivity,
    onLogout,
    resetTimer,
    hasOtherActiveTabs,
    registerActiveTab,
  ])

  useEffect(() => {
    const events = ['click', 'keydown', 'touchstart']

    const addEventListeners = () => {
      events.forEach((event) => {
        window.addEventListener(event, resetTimer, { passive: true })
      })
    }

    const removeEventListeners = () => {
      events.forEach((event) => {
        window.removeEventListener(event, resetTimer)
      })
    }

    window.addEventListener('storage', handleStorageChange)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleWindowFocus)
    window.addEventListener('pageshow', handlePageShow)

    addEventListeners()

    registerActiveTab()

    if (checkOtherTabsInactivity()) {
      onLogout()
      return
    }

    resetTimer()

    return () => {
      removeEventListeners()
      window.removeEventListener('storage', handleStorageChange)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleWindowFocus)
      window.removeEventListener('pageshow', handlePageShow)
      clearTimers()
      clearToast()
      resetState()
      unregisterActiveTab()
    }
  }, [
    resetTimer,
    clearTimers,
    clearToast,
    resetState,
    handleStorageChange,
    handleVisibilityChange,
    handleWindowFocus,
    handlePageShow,
    checkOtherTabsInactivity,
    onLogout,
    registerActiveTab,
    unregisterActiveTab,
  ])

  return
}
